import { InnerConfigObjectType } from '@shared/config/types';

/**
 * Performs a deep merge of the source object into the target object.
 * This function recursively merges nested objects, creating a new object
 * without mutating the original target or source. If a key exists in both
 * objects and both values are objects, they are merged recursively.
 * Otherwise, the source value overwrites the target value.
 *
 * Handles cases where target or source are not objects (e.g., primitives),
 * returning the appropriate value based on the merge logic.
 *
 * @param target - The base object to merge into. Can be an object or a primitive.
 * @param source - The object to merge from. Can be an object, a primitive, or undefined.
 * @returns A new object resulting from the deep merge, or the source value if target is not an object.
 *
 * @example
 * const target = { a: 1, b: { c: 2 } };
 * const source = { b: { d: 3 }, e: 4 };
 * const result = deepMerge(target, source);
 * // result: { a: 1, b: { c: 2, d: 3 }, e: 4 }
 *
 */
export const deepMerge = (
  target: InnerConfigObjectType,
  source: InnerConfigObjectType,
) => {
  // If source is not an object, return it (base case for primitives)
  if (typeof source !== 'object' || source === null) {
    return source;
  }

  // If target is not an object, return source (handles cases where target is primitive)
  if (typeof target !== 'object' || target === null) {
    return source;
  }

  // Create a shallow copy of the target to avoid mutating the original
  const result = { ...target };

  // Iterate through source keys
  for (const key in source) {
    // If both are objects, recurse; otherwise, assign source value
    if (
      typeof source[key] === 'object' &&
      source[key] !== null &&
      typeof target[key] === 'object' &&
      target[key] !== null
    ) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }

  return result;
};
